/* eslint-disable  */
import { debounce } from "lodash";
import React, { useEffect, useState } from "react";
import ReactPaginate from "react-paginate";
import { useDispatch, useSelector } from "react-redux";
import { bindActionCreators } from "redux";
import { Search, ChevronRight } from "lucide-react";
import Loader from "../../Components/Loader";
import { dataActionCreators } from "../../services/Actions";
import RenderLoanPage from "../RenderLoanPage";
import { ToastContainer, toast } from "react-toastify";
import { GhanaMoneyDisplay } from "../../helpers/helper";
import { GetTotal } from "../../services/Actions/dataAction";
import CohortFilter, {
  COHORT,
  cohortQuery,
  FILTER_INPUT_CLASS,
  FILTER_SELECT_CLASS,
} from "../../Components/CohortFilter";

function LoansDeduction() {
  const dispatch = useDispatch();
  const { GetAllLoans, ResetDataResponse } = bindActionCreators(dataActionCreators, dispatch);
  const [limit, setLimit] = useState(20);
  const [search, setSearch] = useState(null);
  // Members or UPNMG staff. Every GetAllLoans call below goes through
  // cohortQuery() so switching cohort cannot leave one call site behind and
  // desync the list from the filter.
  const [cohort, setCohort] = useState(COHORT.MEMBERS);

  const [currentItems, setCurrentItems] = useState(null);
  const [pageCount, setPageCount] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const itemsPerPage = limit;
  const data = useSelector((state) => state.data);
  const { loans, paginate, response, total_loans } = data;

  useEffect(() => {
    const endOffset = itemOffset + itemsPerPage;
    setCurrentItems(loans?.slice(itemOffset, endOffset));
    setPageCount(Number(paginate?.total));
  }, [itemOffset, itemsPerPage, loans]);

  const handlePageClick = (event) => {
    GetAllLoans(true, event.selected + 1, limit, cohortQuery(search, cohort));
    const newOffset = (event.selected * itemsPerPage) % paginate?.total;
    setItemOffset(newOffset);
  };

  const onSearchTextChange = debounce((e) => {
    if (e?.target?.value) {
      setSearch(e?.target?.value ?? null);
      GetAllLoans(true, 1, limit, cohortQuery(e?.target?.value, cohort));
    } else {
      setSearch(null);
      GetAllLoans(true, 1, limit, cohortQuery(null, cohort));
    }
  }, 800);

  useEffect(() => {
    GetAllLoans(true, 1, limit, cohortQuery(search, cohort));
  }, []);

  // Also the cohort switch's refetch — the toggle has no handler of its own.
  // GetTotal goes with it: the Total tile falls back to the dashboard-wide
  // figure whenever the listing omits one (it only returns a total on the
  // search branch), so leaving it behind would print a member total over a
  // staff table.
  useEffect(() => {
    GetAllLoans(true, 1, limit, cohortQuery(search, cohort));
    dispatch(GetTotal(cohort));
  }, [limit, cohort]);

  useEffect(() => {
    if (response?.state === "SUCCESS") {
      toast.success(response?.message);
      setTimeout(() => ResetDataResponse(), 1500);
    } else if (response?.state === "ERROR") {
      toast.error(response?.message);
      setTimeout(() => ResetDataResponse(), 1500);
    }
  }, [response?.state]);

  return (
    <RenderLoanPage title="Loans Deductions">

      {/* SEARCH & FILTER BAR */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" size={16} />
          <input
            type="search"
            placeholder="Search by Staff ID.."
            className={FILTER_INPUT_CLASS}
            onChange={onSearchTextChange}
          />
        </div>
        <CohortFilter
          value={cohort}
          onChange={setCohort}
          label="Deductions for"
          variant="segmented"
        />
        <select
          className={FILTER_SELECT_CLASS}
          onChange={(e) => setLimit(e.target.value)}
        >
          <option value={20}>20 per page</option>
          <option value={50}>50 per page</option>
          <option value={100}>100 per page</option>
          <option value={250}>250 per page</option>
          <option value={500}>500 per page</option>
        </select>
      </div>

      <section className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
        <div className="p-6 border-b border-slate-50 flex justify-between items-center">
          {/* <h2 className="text-md font-bold text-slate-800">Loans Deductions</h2> */}
          {total_loans > 0 && (
            <span className="text-md font-bold text-slate-700">
              Total: <GhanaMoneyDisplay amount={total_loans} />
            </span>
          )}
        </div>

        {data?.isLoading ? (
          <div className="p-6">
            <Loader />
          </div>
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="dark:bg-gray-800 bg-slate-50">
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Member Info</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Period</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Transaction</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider">Facility</th>
                <th className="px-6 py-4 text-xs font-bold text-slate-500 uppercase tracking-wider text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {data?.loans?.map((loan) => (
                <tr key={loan?._id} className="hover:bg-slate-50/80 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        className="w-10 h-10 rounded-full border border-slate-200 object-cover shadow-sm"
                        src={loan?.user?.profile_image || "/images/dev/avatar1.png"}
                        alt="Profile"
                      />
                      <div>
                        <p className="text-sm font-bold text-slate-800 leading-tight">{loan?.user?.name}</p>
                        <p className="text-xs font-medium text-slate-500">{loan?.staff_id}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-50 text-emerald-700 border border-emerald-100">
                      &#8373;{loan?.deduction}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-slate-600 font-medium">{loan?.period}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 font-medium">{loan?.transaction_type}</td>
                  <td className="px-6 py-4 text-sm text-slate-600 font-medium">{loan?.user?.facility}</td>
                  <td className="px-6 py-4 text-right">
                    <button className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-all">
                      <ChevronRight size={20} />
                    </button>
                  </td>
                </tr>
              ))}
              {!data?.loans?.length && (
                <tr>
                  <td colSpan="6" className="px-6 py-14 text-center text-sm text-slate-400">
                    No loan deductions found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        {loans?.length > 0 && (
          <div className="border-t border-slate-100">
            <ReactPaginate
              breakLabel="..."
              nextLabel=">"
              onPageChange={handlePageClick}
              pageRangeDisplayed={5}
              pageCount={pageCount}
              previousLabel="<"
              renderOnZeroPageCount={null}
              containerClassName="flex items-center justify-center gap-1 py-4"
              pageLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
              previousLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
              nextLinkClassName="px-3 py-1.5 rounded-lg text-sm font-medium text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 transition-colors"
              activeLinkClassName="!bg-indigo-600 !text-white shadow-sm"
            />
          </div>
        )}
      </section>
      <ToastContainer autoClose={3000} />
    </RenderLoanPage>
  );
}

export default LoansDeduction;
